import { useState, useEffect, useRef } from "react";
import Title from "../../components/common/title/Title";
import Button from "../../components/common/button/Button";
import { getApi, putApi, postApi } from "../../services/api/globalApi";
import { fileUrl, PublicFileUrl } from "../../services/config";
import {
  successMessage,
  errorMessage,
  warningMessage,
} from "../../utils/toastify-message/ToastifyMessage";

const inputClass =
  "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500";

const labelClass =
  "block mb-2 text-sm font-medium text-gray-900 dark:text-white";

export default function AffiliateSettingsForm() {
  const [videoUrl, setVideoUrl] = useState("");
  const [heroDesc, setHeroDesc] = useState("");
  const [agentCount, setAgentCount] = useState("");
  const [rating, setRating] = useState("");
  const [statYears, setStatYears] = useState("");
  const [statProducts, setStatProducts] = useState("");
  const [statSatisfaction, setStatSatisfaction] = useState("");
  const [phone, setPhone] = useState("");
  const [profitPercent, setProfitPercent] = useState("");
  const [familyTitle, setFamilyTitle] = useState("");
  const [familyDesc, setFamilyDesc] = useState("");
  const [familyImageUrl, setFamilyImageUrl] = useState("");
  const [familyImageFile, setFamilyImageFile] = useState(null);
  const [familyImageUploading, setFamilyImageUploading] = useState(false);
  const familyImageRef = useRef();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    getApi({ path: "admin/affiliate-settings" })
      .then((res) => {
        const d = res?.data?.result;
        setVideoUrl(d?.videoUrl || "");
        setHeroDesc(d?.heroDesc || "");
        setAgentCount(d?.agentCount || "");
        setRating(d?.rating || "");
        setStatYears(d?.statYears || "");
        setStatProducts(d?.statProducts || "");
        setStatSatisfaction(d?.statSatisfaction || "");
        setPhone(d?.phone || "");
        setProfitPercent(d?.profitPercent || "");
        setFamilyTitle(d?.familyTitle || "");
        setFamilyDesc(d?.familyDesc || "");
        setFamilyImageUrl(d?.familyImageUrl || "");
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, []);

  const handleFamilyImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFamilyImageFile(file);
    setFamilyImageUrl(URL.createObjectURL(file));
  };

  const uploadFamilyImage = async () => {
    if (!familyImageFile) return familyImageUrl;
    setFamilyImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", familyImageFile);
      const res = await postApi({ path: `${fileUrl}file`, body: formData });
      return res?.data?.data || null;
    } catch {
      errorMessage("خطا در آپلود تصویر");
      return null;
    } finally {
      setFamilyImageUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim()) return warningMessage("لطفا لینک ویدیو را وارد کنید");

    setLoading(true);
    try {
      let finalFamilyImageUrl = familyImageUrl;
      if (familyImageFile) {
        const filename = await uploadFamilyImage();
        if (!filename) { setLoading(false); return; }
        finalFamilyImageUrl = filename;
        setFamilyImageUrl(filename);
        setFamilyImageFile(null);
      }

      await putApi({
        path: "admin/affiliate-settings",
        body: JSON.stringify({
          videoUrl, heroDesc, agentCount, rating,
          statYears, statProducts, statSatisfaction, phone,
          profitPercent, familyTitle, familyDesc,
          familyImageUrl: finalFamilyImageUrl,
        }),
      });
      successMessage("تنظیمات با موفقیت ذخیره شد");
    } catch (error) {
      console.error(error);
      errorMessage("خطایی رخ داد");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 relative">
      <Title title="تنظیمات صفحه نمایندگی" />
      {fetching ? (
        <p className="mt-6 text-gray-500 text-sm">در حال بارگذاری...</p>
      ) : (
        <form onSubmit={handleSubmit}>

          <div className="grid sm:grid-cols-2 gap-4 mt-6 items-start">
            <div>
              <label className={labelClass}>لینک ویدیو</label>
              <input
                type="url"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://www.aparat.com/video/video/embed/..."
                dir="ltr"
                className={inputClass + " text-left"}
              />
            </div>
            <div>
              <label className={labelClass}>پیش‌نمایش ویدیو</label>
              {videoUrl ? (
                <div
                  className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700"
                  style={{ aspectRatio: "16/9" }}
                >
                  <iframe
                    src={videoUrl}
                    title="پیش‌نمایش ویدیو تنظیمات"
                    allowFullScreen
                    allow="autoplay; fullscreen"
                    className="absolute inset-0 w-full h-full border-0"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-400 text-sm" style={{ aspectRatio: "16/9" }}>
                  پس از وارد کردن لینک نمایش داده می‌شود
                </div>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-6 items-start">
            <div>
              <label className={labelClass}>متن توضیح هیرو</label>
              <textarea
                rows={4}
                value={heroDesc}
                onChange={(e) => setHeroDesc(e.target.value)}
                placeholder="بیش از یک دهه تجربه در تولید محصولات گیاهی..."
                className={inputClass + " resize-y"}
              />
            </div>
            <div>
              <label className={labelClass}>شماره تماس</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="02191095319"
                dir="ltr"
                className={inputClass + " text-left"}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            <div>
              <label className={labelClass}>تعداد نمایندگان فعال</label>
              <input
                type="text"
                value={agentCount}
                onChange={(e) => setAgentCount(e.target.value)}
                placeholder="+۳۲۰"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>امتیاز</label>
              <input
                type="text"
                value={rating}
                onChange={(e) => setRating(e.target.value)}
                placeholder="۴٫۹"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>درصد سود (عدد)</label>
              <input
                type="text"
                value={profitPercent}
                onChange={(e) => setProfitPercent(e.target.value)}
                placeholder="45"
                dir="ltr"
                className={inputClass + " text-left"}
              />
            </div>
          </div>

          <div className="mt-6">
            <label className={labelClass}>آمارهای نوار وسط صفحه</label>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block mb-2 text-xs text-gray-500 dark:text-gray-400">سال سابقه تولید</label>
                <input type="text" value={statYears} onChange={(e) => setStatYears(e.target.value)} placeholder="+۱۰" className={inputClass} />
              </div>
              <div>
                <label className="block mb-2 text-xs text-gray-500 dark:text-gray-400">محصول گیاهی</label>
                <input type="text" value={statProducts} onChange={(e) => setStatProducts(e.target.value)} placeholder="+۹۰" className={inputClass} />
              </div>
              <div>
                <label className="block mb-2 text-xs text-gray-500 dark:text-gray-400">رضایت مشتری</label>
                <input type="text" value={statSatisfaction} onChange={(e) => setStatSatisfaction(e.target.value)} placeholder="۹۸٪" className={inputClass} />
              </div>
            </div>
          </div>

          {/* بخش خانواده */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-bold text-gray-800 dark:text-white mb-4">بخش «خانواده‌ی عطار باشی»</h3>
            <div className="grid sm:grid-cols-2 gap-4 items-start">
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>عنوان بخش</label>
                  <input
                    type="text"
                    value={familyTitle}
                    onChange={(e) => setFamilyTitle(e.target.value)}
                    placeholder="نماینده‌های ما، در کنار هم"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>توضیح بخش</label>
                  <textarea
                    rows={3}
                    value={familyDesc}
                    onChange={(e) => setFamilyDesc(e.target.value)}
                    placeholder="گردهمایی‌ها، دوره‌های آموزشی و رویدادهای فروش..."
                    className={inputClass + " resize-y"}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>تصویر بخش</label>
                <div
                  className="relative rounded-xl overflow-hidden border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 cursor-pointer hover:border-green-400 transition-colors"
                  style={{ aspectRatio: "16/9" }}
                  onClick={() => familyImageRef.current?.click()}
                >
                  {familyImageUrl ? (
                    <img
                      src={familyImageFile ? familyImageUrl : `${PublicFileUrl}public/files/${familyImageUrl}`}
                      alt="تصویر بخش خانواده"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 text-sm gap-2">
                      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      <span>کلیک کنید برای آپلود تصویر</span>
                    </div>
                  )}
                  {familyImageUploading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-sm text-green-600 font-bold">
                      در حال آپلود...
                    </div>
                  )}
                </div>
                <input
                  ref={familyImageRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFamilyImageChange}
                />
                {familyImageUrl && (
                  <button
                    type="button"
                    onClick={() => { setFamilyImageUrl(""); setFamilyImageFile(null); }}
                    className="mt-2 text-xs text-red-500 hover:text-red-700"
                  >
                    حذف تصویر
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <Button
              text={loading ? "در حال ذخیره..." : "ذخیره تنظیمات"}
              type="submit"
              color="success"
              disabled={loading}
            />
          </div>
        </form>
      )}
    </div>
  );
}
